/**
 * Build the team roster with O(1) lookup helpers.
 */

import getQualifiedTeams from './getQualifiedTeams';
import shortnameLookup from './shortnames';
import slugify from '$utils/slugify';

/**
 * Create an empty teams array that still exposes the lookup helpers.
 * @returns {Array}
 */
const emptyTeams = () => {
  const teams = [];
  teams.getTeam = () => null;
  teams.getTeamSlugById = () => undefined;
  return teams;
};

/**
 * Retrieves teams from match data.
 * @param {Object} matchData - The match data object.
 * @returns {Array} - An array of teams, with an attached `getTeam` lookup method.
 */
export default (matchData) => {
  const matches = matchData?.matches?.match;
  if (!Array.isArray(matches)) return emptyTeams();

  const qualifiedTeams = getQualifiedTeams(matches);
  const qualifiedSet = new Set(qualifiedTeams);

  /** @type {Record<string, object>} */
  const countries = {};

  for (const match of matches) {
    const contestants = match?.matchInfo?.contestant;
    if (!Array.isArray(contestants) || contestants.length < 2) continue;

    // Skip matches with placeholder contestants (e.g. "Winner Match 41")
    if (!contestants[0]?.name || !contestants[1]?.name) continue;

    for (const contestant of contestants) {
      const { id, code, name } = contestant || {};
      if (!id || !name || countries[id]) continue;

      const slug = slugify(name);
      countries[id] = {
        id,
        code,
        name,
        shortName: shortnameLookup[name] || name,
        slug,
        // Root-relative, no leading slash; callers do `${base}/${team.path}` (#37).
        path: `teams/${slug}/`,
        qualified: qualifiedSet.has(code),
      };
    }
  }

  // Sort teams alphabetically by name
  const teams = Object.values(countries).sort((a, b) =>
    a.name.localeCompare(b.name)
  );

  // Index teams by every key `getTeam` accepts, so lookups are O(1) instead
  // of a linear `Array.find` scan. This matters for components that call
  // `getTeam` inside loops (Schedule, Bracket, Homepage, etc.).
  const byId = new Map();
  const byCode = new Map();
  const byName = new Map();
  const bySlug = new Map();
  for (const team of teams) {
    byId.set(team.id, team);
    byCode.set(team.code, team);
    byName.set(team.name, team);
    bySlug.set(team.slug, team);
  }

  /**
   * Retrieves a team based on slug, name, code, or ID.
   * @param {string} slugNameCodeOrID - The slug, name, code, or ID of the team.
   * @returns {Object|null} - The team object if found, otherwise null.
   */
  teams.getTeam = (slugNameCodeOrID) => {
    if (!slugNameCodeOrID) return null;
    return (
      byId.get(slugNameCodeOrID) ||
      byCode.get(slugNameCodeOrID) ||
      byName.get(slugNameCodeOrID) ||
      bySlug.get(slugNameCodeOrID) ||
      null
    );
  };

  /**
   * Fast O(1) slug lookup for a team given its Opta id.
   * Prefer this over `getTeam(id)?.slug` in hot paths.
   *
   * @param {string} [id] - The team's Opta id.
   * @returns {string | undefined} - The team's slug, or `undefined` if not found.
   */
  teams.getTeamSlugById = (id) => (id ? byId.get(id)?.slug : undefined);

  return teams;
};
